import { useEffect, useMemo, useState } from 'react';
import { CardGridCardProps } from '@jbhi-fi/components/src/Card/CardGrid/CardGrid';
import { CardTransformer } from '@jbhi-fi/components/src/Contentful/transformers/CardTransformer';
import { useCardEntries } from '@jbhi-fi/components/src/hooks/useCardEntries';
import { ContentfulFilter } from '@jbhi-fi/components/src/hooks/useContentfulEntries/useContentfulEntries';
import { ContentfulEntry } from '@jbhi-fi/apis/src/contentful/types';

export const sortEntriesByPublishedOrCreatedDateDesc = (
  a: Pick<ContentfulEntry<any>, 'fields' | 'sys'>,
  b: Pick<ContentfulEntry<any>, 'fields' | 'sys'>
) =>
  new Date(b.fields?.publishedDate ?? b.sys?.createdAt ?? 0).getTime() -
  new Date(a.fields?.publishedDate ?? a.sys?.createdAt ?? 0).getTime();

const toCardGridCards = ({
  entries,
  totalVisibleCards,
}: {
  entries: Array<ContentfulEntry<any>>;
  totalVisibleCards: number;
}): Array<CardGridCardProps> => {
  return entries
    .slice()
    .sort(sortEntriesByPublishedOrCreatedDateDesc)
    .slice(0, totalVisibleCards)
    .map(CardTransformer.toCardProps);
};

const getLoadingSkeletonCards = (count: number) =>
  Array.from({ length: count }, () => ({
    cover: null,
    isLoading: true,
  }));

export interface UseCards {
  cards: Array<CardGridCardProps>;
  totalCards: number;
  isLoading: boolean;
  loadMoreCards: () => void;
}

export interface UseCardsProps {
  numCards?: number;
  useLoadingSkeletons?: boolean;
  filters: Array<ContentfulFilter>;
}

export const useCards = ({ numCards = 0, useLoadingSkeletons = false, filters }: UseCardsProps): UseCards => {
  const [page, setPage] = useState(1);
  const [cards, setCards] = useState<Array<CardGridCardProps>>([]);
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);
  const {
    entries: results,
    totalResults,
    isLoading,
  } = useCardEntries({
    filters,
    page,
    pageSize: numCards,
    appendResults: true,
    enabled: numCards > 0 && filters.length > 0,
    logPrefix: 'useCards',
  });

  useEffect(() => {
    setPage(1);
    setCards([]);
  }, [filtersKey, numCards]);

  useEffect(() => {
    if (isLoading && useLoadingSkeletons) {
      setCards(cards =>
        cards.concat(getLoadingSkeletonCards(numCards)).slice(0, totalResults === 0 ? numCards : totalResults)
      );
    } else {
      setCards(toCardGridCards({ entries: results, totalVisibleCards: Math.min(totalResults, numCards * page) }));
    }
  }, [isLoading, numCards, page, results, totalResults, useLoadingSkeletons]);

  return {
    cards,
    totalCards: totalResults,
    isLoading,
    loadMoreCards: () => !isLoading && setPage(currentPage => currentPage + 1),
  };
};
